import {
  CustomerServiceOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RiseOutlined,
  ShareAltOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Select, Spin, Tooltip, notification } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { getData, postData } from "@utils/request";
import { BASE_API_URL } from "@utils/constants";
import { ModalChooseTime } from "./ModalChooseTime";

const defaultPayload = { startDate: null, endDate: null, months: 1 };
const dashboardUrl = `${BASE_API_URL}api/mobile/v1/release/top-release`;
const reportingMonthUrl = `${BASE_API_URL}api/mobile/v1/updatemobile/reportingmonthstring`;
const reportingMonthCacheKey = "landing_dashboard_reporting_month_v1";

const formatCompact = (number) => {
  const value = Number(number || 0);
  if (value >= 1000000) {
    return `${(value / 1000000)
      .toFixed(2)
      .replace(/(\.\d*?)0+$/, "$1")
      .replace(/\.$/, "")}M`;
  }
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value;
};

const formatNumber = (number) => Number(number || 0).toLocaleString("en-US");

const getStreamTotal = (dashboard) => {
  if (dashboard?.totalStream !== undefined && dashboard?.totalStream !== null) {
    return dashboard.totalStream;
  }
  return (dashboard?.yearStream || []).reduce(
    (total, point) => total + Number(point.stream || 0),
    0
  );
};

const OverViewLandingPage = () => {
  const { t } = useTranslation();
  const [isModalChooseTime, setIsModalChooseTime] = useState(false);
  const [selectedTime, setSelectedTime] = useState(1);
  const [timePicker, setTimePicker] = useState(null);
  const [optionsTime, setOptionsTime] = useState([]);
  const [timeUpdateTop, setTimeUpdateTop] = useState("");
  const [loading, setLoading] = useState(true);
  const [topRelease, setTopRelease] = useState(null);

  const options = useMemo(
    () => [
      { value: 1, label: t("Last month") },
      { value: 3, label: t("Last 3 months") },
      { value: 12, label: t("Last year") },
      { value: "all", label: t("All times") },
      { value: "custom", label: t("Custom") },
    ],
    [t]
  );

  const loadDashboard = useCallback((payload, forceRefresh = false) => {
    postData({
      url: dashboardUrl,
      method: "POST",
      payload,
      cacheResponse: true,
      forceRefresh,
      setLoading,
      onSuccess: (response) => setTopRelease(response.data),
    });
  }, []);

  useEffect(() => {
    setOptionsTime((previous) => {
      const next = [...options];
      if (selectedTime === "custom" && previous[4])
        next[4].label = previous[4].label;
      return next;
    });
  }, [options, selectedTime]);

  useEffect(() => {
    loadDashboard(defaultPayload);
    getData({
      url: reportingMonthUrl,
      setLoading: () => {},
      onSuccess: (response) => {
        const reportingMonth = moment(
          response.data.reportingMonth,
          "DD/MM/YYYY"
        ).format("MM/YYYY");
        setTimeUpdateTop(reportingMonth);
        const previousMonth = localStorage.getItem(reportingMonthCacheKey);
        localStorage.setItem(reportingMonthCacheKey, reportingMonth);
        if (previousMonth && previousMonth !== reportingMonth)
          loadDashboard(defaultPayload, true);
      },
    });
  }, [loadDashboard]);

  const fetchData = (value, fromModal) => {
    let payload = {
      startDate: null,
      endDate: null,
      months: value === "all" ? null : value,
    };
    if (fromModal) {
      const from = value.filterDate[0].format("MM/YYYY");
      const to = value.filterDate[1].format("MM/YYYY");
      setTimePicker(value.filterDate);
      setSelectedTime("custom");
      setOptionsTime((previous) => {
        const next = [...previous];
        next[4].label = `${from} - ${to}`;
        return next;
      });
      payload = { startDate: from, endDate: to, months: null };
    }
    loadDashboard(payload);
  };

  const handleChangeTime = (value) => {
    if (value === "custom") {
      setIsModalChooseTime(true);
      return;
    }
    setSelectedTime(value);
    setTimePicker(null);
    setOptionsTime(options);
    fetchData(value);
  };

  const handleRefresh = () => {
    const payload =
      selectedTime === "custom" && timePicker
        ? {
            startDate: timePicker[0].format("MM/YYYY"),
            endDate: timePicker[1].format("MM/YYYY"),
            months: null,
          }
        : {
            startDate: null,
            endDate: null,
            months: selectedTime === "all" ? null : selectedTime,
          };
    loadDashboard(payload, true);
  };

  const handleCopyLink = (link) => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      notification.open({
        message: t("Success"),
        description: t("Copy link successful"),
      });
    });
  };

  const tracks = topRelease?.topTracks || [];
  const chartData = useMemo(() => {
    return (Array.isArray(topRelease?.yearStream) ? topRelease.yearStream : [])
      .filter(
        (point) => point && point.year !== undefined && point.stream !== undefined
      )
      .map((point) => ({ ...point, stream: Number(point.stream) }))
      .filter((point) => Number.isFinite(point.stream));
  }, [topRelease]);

  return (
    <ContentStyled>
      <ModalChooseTime
        visible={isModalChooseTime}
        closeModal={() => setIsModalChooseTime(false)}
        fetchData={fetchData}
        setIsModalChooseTime={setIsModalChooseTime}
        timePicker={timePicker}
      />

      <div className="dashboard-content">
        <section className="analytics-panel">
          <div className="section-heading">
            <IconPlate className="gold">
              <RiseOutlined />
            </IconPlate>
            <div>
              <h1>{t("Global stream")}</h1>
              <p>
                {t("From")} 01/2021 {t("to")} {timeUpdateTop || "—"}
              </p>
            </div>
          </div>
          <MetricBadge>
            <span className="metric-icon">
              <CustomerServiceOutlined />
            </span>
            <strong>{formatNumber(getStreamTotal(topRelease))}</strong>
          </MetricBadge>
          <ChartShell>
            {!topRelease ? (
              <Spin indicator={<LoadingOutlined spin />} />
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 16, right: 24, left: 0, bottom: 4 }}
                >
                  <defs>
                    <linearGradient id="streamFill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#079869"
                        stopOpacity={0.38}
                      />
                      <stop
                        offset="100%"
                        stopColor="#079869"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#dfe6ef"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="year"
                    stroke="#7c8597"
                    tickLine={false}
                    axisLine={{ stroke: "#e1e6f0" }}
                    tick={{ fill: "#7c8597", fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={formatCompact}
                    stroke="#7c8597"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "#7c8597", fontSize: 12 }}
                    width={48}
                  />
                  <ChartTooltip
                    formatter={(value) => formatNumber(value)}
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #e1e6f0",
                      background: "#ffffff",
                      boxShadow: "0 6px 18px rgba(13, 27, 42, 0.08)",
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="stream"
                    stroke="#079869"
                    strokeWidth={2.5}
                    fill="url(#streamFill)"
                    isAnimationActive={false}
                    dot={{
                      r: 3.5,
                      fill: "#ffffff",
                      stroke: "#079869",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 5.5,
                      fill: "#079869",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">{t("No data")}</div>
            )}
          </ChartShell>
          <div className="data-note">
            <InfoCircleOutlined />
            <span>{t("publicDashboardDataNote")}</span>
          </div>
        </section>

        <SloganPanel>
          <IconPlate className="gold">
            <RiseOutlined />
          </IconPlate>
          <div>
            <h2>{t("publicDashboardSloganTitle")}</h2>
            <p>{t("publicDashboardSloganBody")}</p>
          </div>
        </SloganPanel>

        <section className="top-section">
          <div className="top-header">
            <div className="section-heading compact">
              <IconPlate className="gold">
                <TrophyOutlined />
              </IconPlate>
              <div>
                <h2>TOP 3</h2>
                <p>{t("publicTop3ByStreams")}</p>
              </div>
            </div>
            <div className="top-controls">
              <Select
                value={selectedTime}
                onChange={handleChangeTime}
                options={optionsTime}
              />
              <Tooltip title={t("Refresh")}>
                <button
                  className="refresh-button"
                  type="button"
                  onClick={handleRefresh}
                  aria-label={t("Refresh")}
                >
                  <ReloadOutlined />
                </button>
              </Tooltip>
            </div>
          </div>

          {loading && tracks.length === 0 ? (
            <EmptyPanel>
              <Spin indicator={<LoadingOutlined spin />} />
            </EmptyPanel>
          ) : tracks.length === 0 ? (
            <EmptyPanel>{t("No data")}</EmptyPanel>
          ) : (
            <div className="track-list">
              <TopTrackHero track={tracks[0]} onShare={handleCopyLink} />
              {tracks.slice(1, 3).map((track, index) => (
                <TopTrackRow
                  key={`${track.link || track.name}-${track.position || index}`}
                  track={track}
                  position={index + 2}
                  onShare={handleCopyLink}
                />
              ))}
            </div>
          )}
        </section>

        <SloganPanel className="bottom-slogan">
          <IconPlate className="gold">
            <ShareAltOutlined />
          </IconPlate>
          <div>
            <h2>{t("publicTop3SloganTitle")}</h2>
            <p>{t("publicTop3SloganBody")}</p>
          </div>
        </SloganPanel>
      </div>
    </ContentStyled>
  );
};

function TopTrackHero({ track, onShare }) {
  return (
    <article className="track-hero">
      <img
        src={track.image}
        alt={track.name || ""}
        loading="eager"
        decoding="async"
      />
      <div className="hero-overlay" />
      <RankBadge className="hero-rank">#{track.position || 1}</RankBadge>
      {track.link && (
        <ShareButton
          className="hero-share"
          onClick={() => onShare(track.link)}
        />
      )}
      <div className="hero-details">
        <StreamBadge dark>
          <CustomerServiceOutlined />
          {formatNumber(track.stream)}
        </StreamBadge>
        <h3>{track.name || "-"}</h3>
        {track.author && <p>{track.author}</p>}
      </div>
    </article>
  );
}

function TopTrackRow({ track, position, onShare }) {
  return (
    <article className="track-row">
      <RankBadge className={`rank-${position}`}>#{position}</RankBadge>
      <img
        src={track.image}
        alt={track.name || ""}
        loading="lazy"
        decoding="async"
      />
      <div className="row-details">
        <h3>{track.name || "-"}</h3>
        {track.author && <p>{track.author}</p>}
        <StreamBadge>
          <CustomerServiceOutlined />
          {formatNumber(track.stream)}
        </StreamBadge>
      </div>
      {track.link && <ShareButton onClick={() => onShare(track.link)} />}
    </article>
  );
}

function StreamBadge({ children, dark = false }) {
  return (
    <span className={`stream-badge ${dark ? "dark" : ""}`}>{children}</span>
  );
}

function ShareButton({ className = "", onClick }) {
  return (
    <button
      className={`share-button ${className}`}
      type="button"
      onClick={onClick}
      aria-label="Share"
    >
      <ShareAltOutlined />
    </button>
  );
}

const ContentStyled = styled.div`
  color-scheme: light;
  --page-top: #fffbf2;
  --page-bottom: #f2f6ff;
  --surface: #ffffff;
  --surface-alt: #f7f9ff;
  --elevated: #f0f4ff;
  --text: #111827;
  --secondary: #4b5565;
  --muted: #7c8597;
  --border: #e1e6f0;
  --grid: rgba(124, 133, 151, 0.18);
  min-height: 100vh;
  color: var(--text);
  background: linear-gradient(180deg, var(--page-top), var(--page-bottom));
  .dashboard-content {
    max-width: 940px;
    margin: 0 auto;
    padding: 16px 0 28px;
  }
  .analytics-panel,
  .top-section,
  .slogan-panel {
    border: 1px solid var(--border);
    border-radius: 14px;
    background: linear-gradient(135deg, var(--surface), var(--surface-alt));
    box-shadow: 0 16px 38px rgba(13, 27, 42, 0.1);
  }
  .analytics-panel {
    padding: 20px;
  }
  .section-heading,
  .top-header,
  .top-controls,
  .track-row,
  .data-note,
  .metric-badge,
  .slogan-panel {
    display: flex;
    align-items: center;
  }
  .section-heading {
    gap: 12px;
  }
  .section-heading h1,
  .section-heading h2,
  .slogan-panel h2,
  .track-hero h3,
  .track-row h3 {
    margin: 0;
    color: var(--text);
  }
  .section-heading h1 {
    font-size: 21px;
    font-weight: 900;
  }
  .section-heading h2 {
    font-size: 20px;
    font-weight: 900;
  }
  .section-heading p,
  .slogan-panel p,
  .track-row p,
  .hero-details p {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12px;
    font-weight: 500;
  }
  .icon-plate {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    flex: 0 0 auto;
  }
  .icon-plate.gold {
    color: #d59a2d;
    background: rgba(213, 154, 45, 0.13);
    border: 1px solid rgba(213, 154, 45, 0.3);
    box-shadow: 0 8px 20px rgba(213, 154, 45, 0.15);
  }
  .metric-badge {
    gap: 12px;
    width: 100%;
    margin-top: 18px;
    padding: 15px;
    border: 1px solid rgba(7, 152, 105, 0.22);
    border-radius: 10px;
    background: linear-gradient(
      135deg,
      rgba(7, 152, 105, 0.1),
      rgba(2, 151, 201, 0.05),
      rgba(255, 255, 255, 0.8)
    );
    box-shadow: 0 10px 24px rgba(7, 152, 105, 0.1);
  }
  .metric-icon {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #079869;
    background: rgba(7, 152, 105, 0.1);
    border: 1px solid rgba(7, 152, 105, 0.28);
    font-size: 21px;
  }
  .metric-badge strong {
    color: #079869;
    font-size: clamp(30px, 5vw, 46px);
    font-weight: 300;
    line-height: 1;
    text-shadow: 0 0 16px rgba(7, 152, 105, 0.25);
  }
  .chart-shell {
    position: relative;
    box-sizing: border-box;
    height: 310px;
    overflow: hidden;
    margin-top: 16px;
    padding: 6px 4px 0;
    border: 1px solid rgba(225, 230, 240, 0.8);
    border-radius: 10px;
    background: rgba(240, 244, 255, 0.52);
  }
  .chart-shell > .ant-spin {
    display: grid;
    place-items: center;
    height: 100%;
    color: #d59a2d;
  }
  .chart-empty {
    display: grid;
    place-items: center;
    height: 100%;
    color: var(--muted);
    font-size: 12px;
  }
  .data-note {
    gap: 6px;
    margin-top: 10px;
    color: var(--muted);
    font-size: 11px;
    line-height: 1.4;
  }
  .slogan-panel {
    gap: 12px;
    margin-top: 16px;
    padding: 15px;
    background: linear-gradient(
      135deg,
      var(--surface-alt),
      rgba(213, 154, 45, 0.1),
      rgba(57, 92, 255, 0.06)
    );
  }
  .slogan-panel h2 {
    font-size: 14px;
    font-weight: 900;
    line-height: 1.25;
  }
  .slogan-panel p {
    color: var(--secondary);
    line-height: 1.45;
  }
  .top-section {
    margin-top: 18px;
    padding: 14px;
  }
  .top-header {
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }
  .section-heading.compact {
    gap: 10px;
  }
  .section-heading.compact h2 {
    font-size: 20px;
  }
  .top-controls {
    gap: 10px;
  }
  .top-controls .ant-select-selector {
    height: 38px !important;
    border: 1px solid var(--border) !important;
    border-radius: 9px !important;
    background: var(--elevated) !important;
    color: var(--text) !important;
  }
  .refresh-button,
  .share-button {
    display: grid;
    place-items: center;
    border: 1px solid var(--border);
    cursor: pointer;
    color: #395cff;
    background: var(--elevated);
  }
  .refresh-button {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    font-size: 17px;
  }
  .track-list {
    display: grid;
    gap: 12px;
  }
  .track-hero {
    position: relative;
    aspect-ratio: 1 / 0.82;
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: #121a27;
    box-shadow: 0 16px 30px rgba(13, 27, 42, 0.18);
  }
  .track-hero > img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
  .hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.06) 20%,
      rgba(0, 0, 0, 0.1) 42%,
      rgba(0, 0, 0, 0.88) 100%
    );
  }
  .rank-badge {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    border-radius: 50%;
    font-weight: 900;
  }
  .hero-rank {
    position: absolute;
    top: 14px;
    left: 14px;
    width: 62px;
    height: 62px;
    color: #121212;
    background: linear-gradient(135deg, #ffdf9a, #d59a2d);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 18px rgba(213, 154, 45, 0.28);
    font-size: 18px;
  }
  .hero-share {
    position: absolute;
    top: 14px;
    right: 14px;
  }
  .share-button {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    font-size: 18px;
  }
  .hero-share,
  .stream-badge.dark {
    color: white;
    border-color: rgba(255, 255, 255, 0.14);
    background: rgba(0, 0, 0, 0.5);
  }
  .hero-details {
    position: absolute;
    right: 16px;
    bottom: 16px;
    left: 16px;
    color: white;
  }
  .hero-details h3 {
    margin-top: 10px;
    color: white;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.08;
  }
  .hero-details p {
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
  }
  .stream-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    background: var(--elevated);
    font-size: 16px;
    font-weight: 800;
    line-height: 1;
  }
  .stream-badge.dark {
    border-color: rgba(255, 255, 255, 0.12);
    font-size: 19px;
  }
  .track-row {
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: 11px;
    background: var(--surface);
    box-shadow: 0 8px 18px rgba(13, 27, 42, 0.07);
  }
  .track-row > img {
    width: 62px;
    height: 62px;
    flex: 0 0 auto;
    object-fit: cover;
    border-radius: 9px;
    background: #121a27;
  }
  .track-row .rank-badge {
    width: 44px;
    height: 44px;
    color: #b9c4d6;
    background: var(--elevated);
    border: 1px solid rgba(185, 196, 214, 0.55);
    font-size: 14px;
  }
  .track-row .rank-3 {
    color: #c6813f;
    border-color: rgba(198, 129, 63, 0.55);
  }
  .row-details {
    min-width: 0;
    flex: 1;
  }
  .track-row h3 {
    overflow: hidden;
    font-size: 14px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .track-row p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .track-row .stream-badge {
    margin-top: 7px;
    border: 0;
    padding: 0;
    background: transparent;
    font-size: 16px;
  }
  .track-row .share-button {
    flex: 0 0 auto;
  }
  .empty-panel {
    display: grid;
    place-items: center;
    min-height: 170px;
    border: 1px solid var(--border);
    border-radius: 11px;
    color: var(--muted);
    background: var(--surface);
  }
  @media (max-width: 740px) {
    .dashboard-content {
      padding: 14px 16px 28px;
    }
    .analytics-panel {
      padding: 16px;
    }
    .chart-shell {
      height: 240px;
    }
    .top-header {
      align-items: flex-start;
      flex-direction: column;
    }
    .top-controls,
    .top-controls .ant-select {
      width: 100%;
    }
    .top-controls .ant-select {
      flex: 1;
    }
  }
  @media (max-width: 480px) {
    .dashboard-content {
      padding-right: 10px;
      padding-left: 10px;
    }
    .analytics-panel,
    .top-section,
    .slogan-panel {
      border-radius: 10px;
    }
    .track-hero {
      aspect-ratio: 1 / 1;
    }
    .hero-details h3 {
      font-size: 21px;
    }
  }
`;

const IconPlate = styled.span.attrs({ className: "icon-plate" })``;
const MetricBadge = styled.div.attrs({ className: "metric-badge" })``;
const ChartShell = styled.div.attrs({ className: "chart-shell" })``;
const SloganPanel = styled.section.attrs({ className: "slogan-panel" })``;
const EmptyPanel = styled.div.attrs({ className: "empty-panel" })``;
const RankBadge = styled.span.attrs({ className: "rank-badge" })``;

export default OverViewLandingPage;
